// src/components/dashboards/HODDashboard.jsx - MERGED (Full tabs + Enhanced Class Management)
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api, { leaveAPI, classAPI, classAttendanceAPI, hodAPI, hodExamAPI, hostelInventoryAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import StatCard from '../shared/StatCard';
import '../../styles/Dashboard.css';
import '../../styles/ModalStyles.css';
import LeaveManagementSidebar from '../leave/LeaveManagementSidebar';
import ApproveLeaves from '../leave/ApproveLeaves';
import { ClassTimetableView, ClassCalendarView } from '../shared/ClassTimetableCalendar';
import { exportTimetableToPDF } from '../../utils/timetablePDF';
import MyBorrowedBooks from '../shared/MyBorrowedBooks';
import HostelUserPortalTab from '../hostel/HostelUserPortalTab';
import StudentActivityModal from '../professor/activities/modals/StudentActivityModal';
import Calendar from '../shared/Calendar';

// ─── HOD Class Details & Attendance Roster Modal ───────────────────────────
function HODClassDetailsModal({ classItem, onClose }) {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/auth/class-attendance/?class_id=${classItem.id}`)
      .then(res => {
        if (active) setAttendance(Array.isArray(res.data) ? res.data : (res.data?.results || []));
      })
      .catch(err => console.error('Failed to load class attendance roster:', err))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [classItem.id]);

  const presentCount = attendance.filter(r => r.status === 'PRESENT' || r.status === 'LATE').length;
  const absentCount  = attendance.filter(r => r.status === 'ABSENT').length;
  const totalCount   = attendance.length;
  const attPct       = totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0;

  const filteredRoster = attendance.filter(r => {
    if (statusFilter === 'PRESENT') return r.status === 'PRESENT' || r.status === 'LATE';
    if (statusFilter === 'ABSENT') return r.status === 'ABSENT';
    return true;
  });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)', zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#ffffff', borderRadius: 16, border: '1px solid #e2e8f0', width: '100%', maxWidth: 780, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
        
        {/* Header */}
        <div style={{ padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{classItem.title} — Session Details & Attendance</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              MBBS Year {classItem.mbbs_year} · {classItem.department_name} · Date: {classItem.start_date}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>✕</button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
          
          {/* Metadata Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, background: '#f1f5f9', padding: 14, borderRadius: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Class Type:</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: (classItem.class_type === 'Lab/Practical' || classItem.class_type === 'Practical') ? '#6b21a8' : '#1d4ed8' }}>
                {(classItem.class_type === 'Lab/Practical' || classItem.class_type === 'Practical') ? '🔬 Lab/Practical' : '📖 Theory'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Topic Taught:</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>{classItem.topic || classItem.topic_name || 'General Session'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Assigned Faculty:</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{classItem.assigned_to_name || 'Unassigned'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Time Slot:</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{classItem.time_slot_name || 'Regular'} ({classItem.start_date})</div>
            </div>
          </div>

          {/* Attendance Summary Bar */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Session Attendance Rate: </span>
              <span style={{ fontSize: 14, fontWeight: 800, color: attPct >= 75 ? '#166534' : '#991b1b' }}>{attPct}%</span>
              <span style={{ fontSize: 11.5, color: '#64748b', marginLeft: 8 }}>({presentCount} Present / {absentCount} Absent / {totalCount} Total)</span>
            </div>

            {/* Filter Pills */}
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setStatusFilter('ALL')} style={{ padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, border: '1px solid #cbd5e1', background: statusFilter === 'ALL' ? '#1d4ed8' : '#fff', color: statusFilter === 'ALL' ? '#fff' : '#475569', cursor: 'pointer' }}>
                All ({totalCount})
              </button>
              <button onClick={() => setStatusFilter('PRESENT')} style={{ padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, border: '1px solid #86efac', background: statusFilter === 'PRESENT' ? '#22c55e' : '#fff', color: statusFilter === 'PRESENT' ? '#fff' : '#166534', cursor: 'pointer' }}>
                ✓ Present ({presentCount})
              </button>
              <button onClick={() => setStatusFilter('ABSENT')} style={{ padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, border: '1px solid #fecaca', background: statusFilter === 'ABSENT' ? '#ef4444' : '#fff', color: statusFilter === 'ABSENT' ? '#fff' : '#991b1b', cursor: 'pointer' }}>
                ✗ Absent ({absentCount})
              </button>
            </div>
          </div>

          {/* Attendance Roster Table */}
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Loading session attendance roster…</div>
          ) : filteredRoster.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: 10, border: '1px dashed #cbd5e1', fontSize: 13 }}>
              No attendance records logged for this session filter.
            </div>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', color: '#64748b', fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '10px 14px' }}>Student Name</th>
                    <th style={{ padding: '10px 14px' }}>Student ID</th>
                    <th style={{ padding: '10px 14px' }}>Status</th>
                    <th style={{ padding: '10px 14px' }}>Notes / Source</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRoster.map((r, idx) => (
                    <tr key={r.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: '#0f172a' }}>{r.student_name}</td>
                      <td style={{ padding: '10px 14px', color: '#475569' }}>{r.student_id_num || r.student_id_number || r.student_id || '—'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{
                          padding: '3px 9px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                          background: (r.status === 'PRESENT' || r.status === 'LATE') ? '#dcfce7' : '#fee2e2',
                          color: (r.status === 'PRESENT' || r.status === 'LATE') ? '#166534' : '#991b1b'
                        }}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: 11.5, color: '#64748b' }}>{r.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', fontWeight: 600, fontSize: 12, cursor: 'pointer', color: '#334155' }}>
            Close Modal
          </button>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────────
   CLASS MANAGEMENT HELPERS & CONSTANTS (from File 2)
───────────────────────────────────────────────────────────────────────────── */
const STATUS_META = {
  PENDING:   { color: '#92400e', bg: '#fef3c7', label: 'Pending',   icon: '⏳' },
  ONGOING:   { color: '#1d4ed8', bg: '#dbeafe', label: 'Ongoing',   icon: '▶' },
  COMPLETED: { color: '#166534', bg: '#dcfce7', label: 'Completed', icon: '✓' },
  CANCELLED: { color: '#6b7280', bg: '#f3f4f6', label: 'Cancelled', icon: '✕' },
};
const MBBS_YEAR_LABELS = { 1: '1st Year', 2: '2nd Year', 3: '3rd Year', 4: '4th Year' };

const fmtDate = s => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDT   = s => s ? new Date(s).toLocaleString('en-IN',  { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.PENDING;
  return (
    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: m.bg, color: m.color }}>
      {m.icon} {m.label}
    </span>
  );
};

/* ── Modal inline styles ──────────────────────────────────────────────────── */
const M = {
  overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 },
  modal:    { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 500, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 24px 60px #0005' },
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid #f1f5f9' },
  title:    { fontSize: 17, fontWeight: 800, color: '#0f172a', margin: 0 },
  close:    { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 },
  classInfo:{ margin: '16px 20px', background: '#f8fafc', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 6 },
  ciRow:    { display: 'flex', gap: 12, alignItems: 'center', fontSize: 13 },
  ciLabel:  { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', width: 40, flexShrink: 0 },
  ciVal:    { fontWeight: 700, color: '#0f172a' },
  err:      { margin: '0 20px 12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '8px 12px', borderRadius: 8, fontSize: 13 },
  selLabel: { display: 'block', margin: '0 20px 8px', fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.04em' },
  facList:  { margin: '0 20px', display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' },
  facRow:   { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: '1.5px solid', borderRadius: 10, cursor: 'pointer', transition: 'all .1s' },
  footer:   { display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px 20px', borderTop: '1px solid #f1f5f9', marginTop: 16 },
  cancelBtn:{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' },
  assignBtn:{ padding: '9px 22px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

function MultiAssignModal({ classes, deptFaculty, onClose, onAssigned, classAPI }) {
  // Group classes by title (same title = same course/subject, different dates = sessions)
  const grouped = useMemo(() => {
    const map = {};
    classes.forEach(c => {
      const key = `${c.mbbs_year}-${c.department_code}-${c.title.split('·')[0].trim()}`;
      if (!map[key]) map[key] = { label: c.title.split('·')[0].trim(), sessions: [] };
      map[key].sessions.push(c);
    });
    return Object.values(map);
  }, [classes]);

  // assignments: { [classId]: facultyId }
  const [assignments, setAssignments] = useState({});
  const [titleMap,    setTitleMap]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [results, setResults] = useState(null);

  const setFaculty = (classId, facId) => {
    setAssignments(prev => ({ ...prev, [classId]: facId }));
  };

  const applyToAll = (facId) => {
    const all = {};
    classes.forEach(c => { all[c.id] = facId; });
    setAssignments(all);
  };

  const handleSave = async () => {
    const toAssign = Object.entries(assignments).filter(([, fid]) => fid);
    if (toAssign.length === 0) { setError('Assign at least one session.'); return; }
    setSaving(true); setError('');
    let ok = 0, fail = [];
    for (const [classId, facId] of toAssign) {
      try {
        await classAPI.assignClass(parseInt(classId), { faculty_id: parseInt(facId), title: (titleMap[classId] || '').trim() });
        ok++;
      } catch (e) {
        fail.push({ classId, error: e.response?.data?.error || 'Failed' });
      }
    }
    setSaving(false);
    setResults({ ok, fail });
    if (ok > 0) setTimeout(onAssigned, 800);
  };

  const M = {
    overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 },
    modal:    { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 60px #0005' },
    header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid #f1f5f9' },
    title:    { fontSize: 17, fontWeight: 800, color: '#0f172a', margin: 0 },
    close:    { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' },
  };

  return (
    <div style={M.overlay} onClick={onClose}>
      <div style={M.modal} onClick={e => e.stopPropagation()}>
        <div style={M.header}>
          <h3 style={M.title}>Multi-Session Faculty Assignment</h3>
          <button style={M.close} onClick={onClose}>×</button>
        </div>

        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
          {/* Quick assign all to one prof */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>Assign All Sessions To:</span>
            <select onChange={e => applyToAll(e.target.value)}
              style={{ flex: 1, minWidth: 200, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', color: '#0f172a', fontFamily: 'inherit' }}>
              <option value="">— Select faculty —</option>
              {deptFaculty.map(f => (
                <option key={f.id} value={f.id}>
                  {f.full_name || `${f.first_name} ${f.last_name}`} · {f.profile?.faculty_subrole?.replace(/_/g,' ')||'Faculty'}
                </option>
              ))}
            </select>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>or assign per session below</span>
          </div>
        </div>

        {error && (
          <div style={{ margin: '12px 20px 0', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>
            {error}
          </div>
        )}
        {results && (
          <div style={{ margin: '12px 20px 0', padding: '10px 14px', borderRadius: 8, fontSize: 13,
            background: results.fail.length === 0 ? '#dcfce7' : '#fef2f2',
            border: `1px solid ${results.fail.length === 0 ? '#b2dcc8' : '#fecaca'}`,
            color: results.fail.length === 0 ? '#166534' : '#991b1b',
          }}>
            {results.ok > 0 && `✓ ${results.ok} session${results.ok!==1?'s':''} assigned. `}
            {results.fail.length > 0 && `✗ ${results.fail.length} failed.`}
          </div>
        )}

        {/* Session list */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {grouped.map((group, gi) => (
            <div key={gi}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                📚 {group.label}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {group.sessions.map(session => (
                  <div key={session.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: assignments[session.id] ? '#eff6ff' : '#f8fafc', borderRadius: 10, border: `1.5px solid ${assignments[session.id] ? '#2563eb' : '#e2e8f0'}` }}>
                    {/* Session info */}
                    <div style={{ minWidth: 60 }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                        {new Date(session.start_date + 'T00:00:00').toLocaleDateString('en-IN', { weekday:'short' })}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{fmtDate(session.start_date)}</div>
                    </div>

                    {session.time_slot_name && (
                      <div style={{ padding: '3px 10px', background: '#dbeafe', color: '#1d4ed8', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                        ⏰ {session.time_slot_name}
                        {session.time_slot_start && ` · ${session.time_slot_start.slice(0,5)}`}
                      </div>
                    )}

                    <StatusBadge status={session.status} />
                    {/* Title input */}
                    <input
                      type="text"
                      value={titleMap[session.id] || ''}
                      onChange={e => setTitleMap(m => ({ ...m, [session.id]: e.target.value }))}
                      placeholder={session.title || 'Session title (optional)'}
                      maxLength={200}
                      style={{ flex: 1, padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, fontFamily: 'inherit', color: '#0f172a', outline: 'none', minWidth: 0 }}
                    />

                    {/* Faculty picker */}
                    <select
                      value={assignments[session.id] || ''}
                      onChange={e => setFaculty(session.id, e.target.value)}
                      style={{ flex: 1, padding: '6px 10px', border: `1px solid ${assignments[session.id] ? '#2563eb' : '#e2e8f0'}`, borderRadius: 8, fontSize: 13, background: '#fff', color: '#0f172a', fontFamily: 'inherit' }}>
                      <option value="">— Assign faculty —</option>
                      {deptFaculty.map(f => (
                        <option key={f.id} value={f.id}>
                          {f.full_name || `${f.first_name} ${f.last_name}`}
                          {session.assigned_to === f.id ? ' (current)' : ''}
                        </option>
                      ))}
                    </select>

                    {session.assigned_to_name && !assignments[session.id] && (
                      <span style={{ fontSize: 11, color: '#2563eb', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {session.assigned_to_name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', padding: '16px 20px', borderTop: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            {Object.values(assignments).filter(Boolean).length} of {classes.length} sessions assigned
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', fontFamily: 'inherit' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || Object.values(assignments).filter(Boolean).length === 0}
              style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1, fontFamily: 'inherit' }}>
              {saving ? 'Saving…' : `✓ Save ${Object.values(assignments).filter(Boolean).length} Assignment${Object.values(assignments).filter(Boolean).length!==1?'s':''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── AssignModal ──────────────────────────────────────────────────────────── */
function AssignModal({ classItem, deptFaculty, onClose, onAssigned, classAPI }) {
  const [selectedFacId, setSelectedFacId] = useState(classItem.assigned_to || '');
  const [topicValue,    setTopicValue]    = useState(classItem.topic || classItem.topic_name || '');
  const [classTypeValue, setClassTypeValue] = useState(classItem.class_type || 'Theory');
  const [assigning,     setAssigning]     = useState(false);
  const [error,         setError]         = useState('');

  const M = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 },
    modal:   { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 60px #0005' },
    header:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid #f1f5f9' },
    close:   { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' },
  };

  const handleAssign = async () => {
    if (!selectedFacId) { setError('Select a faculty member.'); return; }
    setAssigning(true); setError('');
    try {
      await classAPI.assignClass(classItem.id, {
        faculty_id: parseInt(selectedFacId),
        topic: topicValue.trim(),
        topic_name: topicValue.trim(),
        class_type: classTypeValue
      });
      onAssigned(); onClose();
    } catch (e) { setError(e.response?.data?.error || 'Failed to assign.'); }
    finally { setAssigning(false); }
  };

  return (
    <div style={M.overlay} onClick={onClose}>
      <div style={M.modal} onClick={e => e.stopPropagation()}>
        <div style={M.header}>
          <h3 style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', margin: 0 }}>Assign Class to Faculty</h3>
          <button style={M.close} onClick={onClose}>×</button>
        </div>

        <div style={{ margin: '16px 20px', background: '#f8fafc', borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{classItem.title}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            {MBBS_YEAR_LABELS[classItem.mbbs_year]} · {classItem.department_name} · {fmtDate(classItem.start_date)}
          </div>
          {classItem.time_slot_name && (
            <div style={{ fontSize: 12, color: '#1d4ed8', marginTop: 3 }}>⏰ {classItem.time_slot_name}</div>
          )}
        </div>

        {error && <div style={{ margin: '0 20px 12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>{error}</div>}

        {/* 1. Class Type Option (Theory vs Lab/Practical) */}
        <div style={{ margin: '0 20px 14px' }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
            Class Type <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <div style={{ display: 'flex', gap: 12 }}>
            <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', border: `1.5px solid ${classTypeValue==='Theory'?'#2563eb':'#e2e8f0'}`, borderRadius: 10, background: classTypeValue==='Theory'?'#eff6ff':'#fafafa', cursor: 'pointer', fontWeight: 700, fontSize: 13, color: classTypeValue==='Theory'?'#1d4ed8':'#374151' }}>
              <input type="radio" name="classType" value="Theory" checked={classTypeValue==='Theory'} onChange={e => setClassTypeValue(e.target.value)} style={{ accentColor: '#2563eb' }} />
              <span>📖 Theory</span>
            </label>

            <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', border: `1.5px solid ${classTypeValue==='Lab/Practical'?'#2563eb':'#e2e8f0'}`, borderRadius: 10, background: classTypeValue==='Lab/Practical'?'#eff6ff':'#fafafa', cursor: 'pointer', fontWeight: 700, fontSize: 13, color: classTypeValue==='Lab/Practical'?'#1d4ed8':'#374151' }}>
              <input type="radio" name="classType" value="Lab/Practical" checked={classTypeValue==='Lab/Practical'} onChange={e => setClassTypeValue(e.target.value)} style={{ accentColor: '#2563eb' }} />
              <span>🔬 Lab / Practical</span>
            </label>
          </div>
        </div>

        {/* 2. Topic Name Input */}
        <div style={{ margin: '0 20px 14px' }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
            Topic Name <span style={{ color: '#1d4ed8', fontWeight: 500, textTransform: 'none' }}>(Saved with class)</span>
          </label>
          <input
            type="text"
            value={topicValue}
            onChange={e => setTopicValue(e.target.value)}
            placeholder="e.g. Cardiovascular System Physiology or Histology Practical"
            maxLength={255}
            style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', color: '#0f172a' }}
          />
        </div>

        <div style={{ margin: '0 20px', maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {deptFaculty.length === 0 ? (
            <div style={{ padding: 12, background: '#fef9c3', borderRadius: 8, fontSize: 13, color: '#854d0e' }}>
              No faculty found in this department.
            </div>
          ) : deptFaculty.map(f => (
            <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: `1.5px solid ${selectedFacId==f.id?'#2563eb':'#e2e8f0'}`, borderRadius: 10, cursor: 'pointer', background: selectedFacId==f.id?'#eff6ff':'#fafafa' }}>
              <input type="radio" name="faculty" value={f.id} checked={selectedFacId==f.id} onChange={() => setSelectedFacId(f.id)} style={{ accentColor: '#2563eb' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{f.full_name || `${f.first_name} ${f.last_name}`}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{f.profile?.faculty_subrole?.replace(/_/g,' ')||'Faculty'} · {f.email}</div>
              </div>
              {f.id == classItem.assigned_to && <span style={{ fontSize: 11, color: '#2563eb', fontWeight: 700 }}>Current</span>}
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px 20px', borderTop: '1px solid #f1f5f9', marginTop: 16 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleAssign} disabled={assigning || !selectedFacId}
            style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: (assigning||!selectedFacId)?0.6:1, fontFamily: 'inherit' }}>
            {assigning ? 'Assigning…' : '✓ Assign Class'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── ClassCard ────────────────────────────────────────────────────────────── */
function ClassCard({ cs, onAssign, onMultiAssign, sessionCount }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 2 }}>{cs.title}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
            <span style={{
              fontSize: 12,
              fontWeight: 800,
              padding: '3px 10px',
              borderRadius: 8,
              background: (cs.class_type === 'Lab/Practical' || cs.class_type === 'Practical') ? '#f3e8ff' : '#eff6ff',
              color: (cs.class_type === 'Lab/Practical' || cs.class_type === 'Practical') ? '#6b21a8' : '#1d4ed8',
              border: `1.5px solid ${(cs.class_type === 'Lab/Practical' || cs.class_type === 'Practical') ? '#d8b4fe' : '#93c5fd'}`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5
            }}>
              <span>Class Type:</span>
              <span>{(cs.class_type === 'Lab/Practical' || cs.class_type === 'Practical') ? '🔬 Lab/Practical' : '📖 Theory'}</span>
            </span>
            {(cs.topic || cs.topic_name) && (
              <span style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', background: '#f0f9ff', border: '1px solid #bae6fd', padding: '3px 10px', borderRadius: 8 }}>
                Topic: {cs.topic || cs.topic_name}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {MBBS_YEAR_LABELS[cs.mbbs_year]} · {cs.department_name} · {cs.duration_display || 'Custom'}
          </div>
          {cs.time_slot_name && (
            <div style={{ fontSize: 12, color: '#1d4ed8', marginTop: 3 }}>
              ⏰ {cs.time_slot_name} · {cs.time_slot_start?.slice(0,5)} – {cs.time_slot_end?.slice(0,5)}
            </div>
          )}
        </div>
        <StatusBadge status={cs.status} />
      </div>

      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#475569', flexWrap: 'wrap' }}>
        <span>📅 Date: <strong>{fmtDate(cs.start_date)}</strong></span>
        {sessionCount > 1 && (
          <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
            📦 {sessionCount} sessions in this group
          </span>
        )}
      </div>

      <div style={{ background: '#f8fafc', borderRadius: 9, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div>
          {cs.assigned_to_name
            ? <><span style={{ fontSize: 12, color: '#64748b' }}>Assigned to: </span><strong style={{ fontSize: 13, color: '#0f172a' }}>{cs.assigned_to_name}</strong></>
            : <span style={{ fontSize: 13, color: '#ef4444', fontWeight: 600 }}>⚠ Not yet assigned</span>
          }
          {cs.status_note && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Note: {cs.status_note}</div>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => onAssign(cs, 'view_details')}
            style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #3b82f6', background: '#eff6ff', color: '#1d4ed8', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            📊 View Details & Attendance
          </button>
          {cs.status !== 'COMPLETED' && cs.status !== 'CANCELLED' && (
            <button onClick={() => onAssign(cs)}
              style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {cs.assigned_to_name ? '↻ Reassign' : '+ Assign'}
            </button>
          )}
          {sessionCount > 1 && onMultiAssign && (
            <button onClick={onMultiAssign}
              style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #1d4ed8', background: 'transparent', color: '#1d4ed8', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              📦 Multi-Assign
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── StatsBar ─────────────────────────────────────────────────────
function StatsBar({ stats }) {
  return null;
}


// ─── HOD Attendance Summary Tab ───────────────────────────────────────────────
function HODAttendanceSummaryTab() {
  const [classes,      setClasses]      = useState([]);
  const [selClass,     setSelClass]     = useState('');
  const [summary,      setSummary]      = useState([]);
  const [historyDate,  setHistoryDate]  = useState('');
  const [histRecs,     setHistRecs]     = useState([]);
  const [loadingCls,   setLoadingCls]   = useState(true);
  const [loadingSum,   setLoadingSum]   = useState(false);
  const [loadingHist,  setLoadingHist]  = useState(false);
  const [view,         setView]         = useState('summary');
  const [error,        setError]        = useState('');
  const [classSearch,  setClassSearch]  = useState('');
  const [studentSearch,setStudentSearch]= useState('');

  const ATT_COLOR = {
    PRESENT: { c: '#166534', bg: '#dcfce7', border: '#86efac' },
    ABSENT:  { c: '#991b1b', bg: '#fef2f2', border: '#fecaca' },
    LATE:    { c: '#854d0e', bg: '#fef3c7', border: '#fde047' },
    EXCUSED: { c: '#4c1d95', bg: '#ede9fe', border: '#c4b5fd' },
  };

  const S = {
    tab:  (a) => ({ padding: '7px 14px', borderRadius: 8, border: `1.5px solid ${a ? '#1d4ed8' : '#e2e8f0'}`, background: a ? '#dbeafe' : '#fff', color: a ? '#1d4ed8' : '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }),
    card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' },
    th:   { padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
    td:   { padding: '10px 14px', fontSize: 13, borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
    inp:  { padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', color: '#0f172a', outline: 'none', fontFamily: 'inherit' },
  };

  const MBBS_YEAR_LABELS = { 1: '1st Year', 2: '2nd Year', 3: '3rd Year', 4: '4th Year' };

  useEffect(() => {
    classAPI.getHODClasses({})
      .then(d => setClasses(Array.isArray(d) ? d : (d.results || [])))
      .catch(() => {})
      .finally(() => setLoadingCls(false));
  }, []);

  useEffect(() => {
    if (!selClass) return;
    setLoadingSum(true); setError('');
    classAttendanceAPI.getSummary(selClass)
      .then(d => setSummary(Array.isArray(d) ? d : []))
      .catch(() => setError('Failed to load summary.'))
      .finally(() => setLoadingSum(false));
  }, [selClass]);

  useEffect(() => {
    if (!selClass || !historyDate || view !== 'datewise') return;
    setLoadingHist(true);
    classAttendanceAPI.getForClass(selClass, historyDate)
      .then(d => setHistRecs(Array.isArray(d) ? d : []))
      .catch(() => setHistRecs([]))
      .finally(() => setLoadingHist(false));
  }, [selClass, historyDate, view]);

  const pctColor = p => p >= 75 ? '#166534' : p >= 50 ? '#854d0e' : '#991b1b';
  const pctBg    = p => p >= 75 ? '#dcfce7' : p >= 50 ? '#fef3c7' : '#fef2f2';

  // Filtered class list for dropdown
  const filteredClasses = classes.filter(c =>
    !classSearch.trim() ||
    c.title?.toLowerCase().includes(classSearch.toLowerCase()) ||
    c.department_name?.toLowerCase().includes(classSearch.toLowerCase()) ||
    (MBBS_YEAR_LABELS[c.mbbs_year] || '').toLowerCase().includes(classSearch.toLowerCase())
  );

  // Filtered student summary
  const filteredSummary = summary.filter(r =>
    !studentSearch.trim() ||
    (r.name || '').toLowerCase().includes(studentSearch.toLowerCase()) ||
    (r.student_id_num || '').toLowerCase().includes(studentSearch.toLowerCase())
  );

  const belowThreshold = filteredSummary.filter(r => r.attendance_pct < 75);

  const dPresent = histRecs.filter(r => r.status === 'PRESENT' || r.status === 'LATE').length;
  const dAbsent  = histRecs.filter(r => r.status === 'ABSENT').length;
  const dPct     = histRecs.length > 0 ? Math.round(dPresent / histRecs.length * 100) : 0;

  const selectedClassObj = classes.find(c => c.id == selClass);

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Class search + selector */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '0 0 200px' }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5 }}>Search class</label>
          <input
            type="text"
            value={classSearch}
            onChange={e => setClassSearch(e.target.value)}
            placeholder="Filter by title, dept…"
            style={{ ...S.inp, width: '100%', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ flex: '1 1 240px' }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5 }}>Class</label>
          {loadingCls ? <span style={{ fontSize: 13, color: '#94a3b8' }}>Loading…</span> : (
            <select value={selClass} onChange={e => { setSelClass(e.target.value); setStudentSearch(''); }}
              style={{ ...S.inp, width: '100%', cursor: 'pointer' }}>
              <option value="">— Select class ({filteredClasses.length} shown) —</option>
              {filteredClasses.map(c => (
                <option key={c.id} value={c.id}>
                  {c.title} · {MBBS_YEAR_LABELS[c.mbbs_year]} · {c.department_code || c.department_name} · {c.status}
                </option>
              ))}
            </select>
          )}
        </div>
        {selClass && (
          <div style={{ display: 'flex', gap: 6 }}>
            {[['summary', 'Overall Summary'], ['datewise', '📅 Date-wise']].map(([k, l]) => (
              <button key={k} onClick={() => setView(k)} style={S.tab(view === k)}>{l}</button>
            ))}
          </div>
        )}
      </div>

      {/* Selected class info pill */}
      {selClass && selectedClassObj && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#1d4ed8', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <strong>{selectedClassObj.title}</strong>
          <span>{MBBS_YEAR_LABELS[selectedClassObj.mbbs_year]}</span>
          <span>{selectedClassObj.department_name}</span>
          <span>Status: {selectedClassObj.status}</span>
          {selectedClassObj.assigned_to_name && <span>Faculty: {selectedClassObj.assigned_to_name}</span>}
        </div>
      )}

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>{error}</div>}

      {!selClass ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>
          Search and select a class above to view attendance statistics.
        </div>

      ) : view === 'summary' ? (
        loadingSum ? <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>Loading…</div> :
        summary.length === 0 ? <div style={{ textAlign: 'center', padding: 36, color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>No attendance records yet.</div> : (
          <>
            {belowThreshold.length > 0 && (
              <div style={{ background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#991b1b', fontWeight: 600 }}>
                ⚠ {belowThreshold.length} student{belowThreshold.length !== 1 ? 's' : ''} below 75%: {belowThreshold.map(r => r.name || r.student_id_num).join(', ')}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                ['Students',       summary.length,                                                          '#334155', '#f1f5f9'],
                ['≥75% On track',  summary.filter(r => r.attendance_pct >= 75).length,                     '#166534', '#dcfce7'],
                ['50–74% At risk', summary.filter(r => r.attendance_pct >= 50 && r.attendance_pct < 75).length, '#854d0e', '#fef3c7'],
                ['<50% Critical',  summary.filter(r => r.attendance_pct < 50).length,                      '#991b1b', '#fef2f2'],
              ].map(([l, v, c, bg]) => (
                <div key={l} style={{ flex: '1 1 110px', background: bg, borderRadius: 12, padding: '12px 16px' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: c, lineHeight: 1 }}>{v}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: c, marginTop: 3 }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Student search */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                type="text"
                value={studentSearch}
                onChange={e => setStudentSearch(e.target.value)}
                placeholder="🔍 Search student by name or ID…"
                style={{ ...S.inp, flex: 1, maxWidth: 320 }}
              />
              {studentSearch && (
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {filteredSummary.length} of {summary.length} students
                </span>
              )}
              {studentSearch && (
                <button onClick={() => setStudentSearch('')}
                  style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151', fontFamily: 'inherit' }}>
                  ✕ Clear
                </button>
              )}
            </div>

            <div style={S.card}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                  <thead style={{ background: '#f8fafc' }}>
                    <tr>{['Student', 'ID', 'Total Sessions', 'Present', 'Absent', 'Excused', 'Attendance %'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {filteredSummary.length === 0 ? (
                      <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No students match your search.</td></tr>
                    ) : (
                      filteredSummary.slice().sort((a, b) => a.attendance_pct - b.attendance_pct).map((row, i) => {
                        const p = row.attendance_pct;
                        return (
                          <tr key={row.student_id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ ...S.td, fontWeight: 600, color: '#0f172a' }}>{row.name || '—'}</td>
                            <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{row.student_id_num || '—'}</td>
                            <td style={S.td}>{row.total}</td>
                            <td style={{ ...S.td, fontWeight: 700, color: '#166534' }}>{row.present}</td>
                            <td style={{ ...S.td, fontWeight: 700, color: '#991b1b' }}>{row.absent}</td>
                            <td style={{ ...S.td, fontWeight: 700, color: '#4c1d95' }}>{row.excused}</td>
                            <td style={S.td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ flex: 1, height: 7, background: '#e5e7eb', borderRadius: 4 }}>
                                  <div style={{ width: `${p}%`, height: '100%', background: pctColor(p), borderRadius: 4, transition: 'width .3s' }} />
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 700, color: pctColor(p), background: pctBg(p), padding: '2px 8px', borderRadius: 20, minWidth: 44, textAlign: 'center' }}>{p}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )

      ) : (
        /* DATE-WISE VIEW */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5 }}>Select Date</label>
              <input type="date" value={historyDate} onChange={e => setHistoryDate(e.target.value)}
                style={{ ...S.inp }} />
            </div>
          </div>

          {!historyDate ? (
            <div style={{ textAlign: 'center', padding: '36px 24px', color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>Select a date to view attendance details.</div>
          ) : loadingHist ? (
            <div style={{ textAlign: 'center', padding: 36, color: '#64748b' }}>Loading…</div>
          ) : histRecs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 36, color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>No records for {historyDate}.</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[[`✓ Present (${dPresent})`, '#166534', '#dcfce7'], [`✗ Absent (${dAbsent})`, '#991b1b', '#fef2f2'], [`${dPct}% Rate`, '#1d4ed8', '#dbeafe']].map(([l, c, bg]) => (
                  <div key={l} style={{ flex: '1 1 110px', background: bg, borderRadius: 12, padding: '12px 16px' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: c, lineHeight: 1 }}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={S.card}>
                <div style={{ padding: '12px 18px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#166534', background: '#f0fdf4' }}>✓ Present — {historyDate} ({dPresent})</div>
                <div style={{ padding: '12px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8 }}>
                  {histRecs.filter(r => r.status === 'PRESENT' || r.status === 'LATE').map(r => (
                    <div key={r.id} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${ATT_COLOR[r.status]?.border || '#86efac'}`, background: ATT_COLOR[r.status]?.bg, fontSize: 13, fontWeight: 700, color: '#0f172a', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{r.student_name}</span>
                      {r.status === 'LATE' && <span style={{ fontSize: 10, color: '#854d0e', fontWeight: 800 }}>LATE</span>}
                    </div>
                  ))}
                  {dPresent === 0 && <div style={{ color: '#94a3b8', fontSize: 13 }}>None</div>}
                </div>
              </div>
              <div style={S.card}>
                <div style={{ padding: '12px 18px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#991b1b', background: '#fef2f2' }}>✗ Absent — {historyDate} ({dAbsent})</div>
                <div style={{ padding: '12px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8 }}>
                  {histRecs.filter(r => r.status === 'ABSENT').map(r => (
                    <div key={r.id} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{r.student_name}</div>
                  ))}
                  {dAbsent === 0 && <div style={{ color: '#94a3b8', fontSize: 13 }}>None</div>}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── HODClassesTab ────────────────────────────────────────────────────────── */
export function HODClassesTab({ classAPI }) {
  const [classes,       setClasses]       = useState([]);
  const [stats,         setStats]         = useState({});
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [statusFilter,  setStatusFilter]  = useState('all');
  const [yearFilter,    setYearFilter]    = useState('all');
  const [assignTarget,  setAssignTarget]  = useState(null); // single assign
  const [multiTarget,   setMultiTarget]   = useState(null); // multi-assign sessions
  const [detailTarget,  setDetailTarget]  = useState(null); // view class details & attendance
  const [deptFaculty,   setDeptFaculty]   = useState([]);
  const [viewMode,      setViewMode]      = useState('timetable'); // 'timetable' | 'calendar'

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (yearFilter !== 'all') params.year = yearFilter;
      const [data, statsData] = await Promise.all([
        classAPI.getHODClasses(params),
        classAPI.getHODStats(),
      ]);
      setClasses(Array.isArray(data) ? data : (data.results || []));
      setStats(statsData || {});
    } catch { setError('Failed to load classes.'); }
    finally { setLoading(false); }
  }, [statusFilter, yearFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    classAPI.getDeptFaculty()
      .then(d => setDeptFaculty(Array.isArray(d) ? d : (d.results || [])))
      .catch(() => {});
  }, []);

  const handleAssigned = () => { setAssignTarget(null); setMultiTarget(null); load(); };

  // ── Group classes by subject/slot for multi-session view ────────
  const sessionGroups = useMemo(() => {
    // Key: dept-year-slotName (if available), else individual
    const map = {};
    classes.forEach(c => {
      const slotBase = c.time_slot_name ? c.time_slot_name.split('·')[0].trim() : 'no-slot';
      const key = `${c.mbbs_year}-${c.department_code}-${slotBase}`;
      if (!map[key]) map[key] = [];
      map[key].push(c);
    });
    // Sort each group by date
    Object.values(map).forEach(arr => arr.sort((a, b) => (a.start_date||'') > (b.start_date||'') ? 1 : -1));
    return map;
  }, [classes]);

  // ── Flat grouped by status ──────────────────────────────────────
  const groupedByStatus = useMemo(() => {
    const g = { PENDING: [], ONGOING: [], COMPLETED: [], CANCELLED: [] };
    classes.forEach(c => { if (g[c.status]) g[c.status].push(c); });
    return g;
  }, [classes]);

  // ── Timeline: classes grouped by date ──────────────────────────
  const timelineGroups = useMemo(() => {
    const map = {};
    classes.forEach(c => {
      const d = c.start_date || 'Unknown';
      if (!map[d]) map[d] = [];
      map[d].push(c);
    });
    return Object.entries(map).sort(([a],[b]) => a > b ? 1 : -1);
  }, [classes]);

  // Unassigned count for attention banner
  const unassigned = classes.filter(c => !c.assigned_to_name && c.status !== 'CANCELLED').length;

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Attention banner */}
      {unassigned > 0 && (
        <div style={{ background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#991b1b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10 }}>
          ⚠ {unassigned} class{unassigned !== 1 ? 'es' : ''} {unassigned !== 1 ? 'are' : 'is'} not yet assigned to faculty.
          <button onClick={() => setStatusFilter('PENDING')} style={{ marginLeft: 'auto', padding: '4px 12px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#991b1b', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            View Pending
          </button>
        </div>
      )}

      {/* Filter + view bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Status filter dropdown */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '6px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, background: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, color: '#374151' }}
          >
            <option value="all">All Statuses</option>
            {Object.entries(STATUS_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>

          {/* Year filter */}
          <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}
            style={{ padding: '6px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="all">All Years</option>
            {[1,2,3,4].map(y => <option key={y} value={y}>{MBBS_YEAR_LABELS[y]}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* View mode */}
          <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            {[['timetable','🗓 Timetable'],['calendar','📆 Calendar']].map(([mode, label]) => (
              <button key={mode} onClick={() => setViewMode(mode)}
                style={{ padding: '6px 12px', border: 'none', background: viewMode===mode?'#1d4ed8':'#fff', color: viewMode===mode?'#fff':'#374151', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', borderRight: '1px solid #e2e8f0' }}>
                {label}
              </button>
            ))}
          </div>


          <button
            onClick={() => exportTimetableToPDF({ classes, title: 'HOD Department Timetable', institutionName: 'Medical College ERP' })}
            style={{ padding:'7px 14px', borderRadius:8, border:'1px solid #334155', background:'#fff', cursor:'pointer', fontSize:12, fontWeight:700, color:'#334155', fontFamily:'inherit', flexShrink:0 }}>
            🖨 Export PDF
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>
          {error}
        </div>
      )}

      {loading && classes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading department classes…</div>
      ) : classes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px 24px', color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>
          No classes found for your department{statusFilter !== 'all' ? ` with status "${STATUS_META[statusFilter]?.label}"` : ''}.
          Classes are created by the Principal.
        </div>
      ) : (
        <div style={{ position: 'relative', opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          {/* ── Timetable view ── */}
          {viewMode === 'timetable' ? (
            <ClassTimetableView
              classes={classes}
              role="HOD"
              onAssignClick={(cs, mode) => mode === 'view_details' ? setDetailTarget(cs) : setAssignTarget(cs)}
            />
          ) : viewMode === 'calendar' ? (
            <ClassCalendarView
              classes={classes}
              role="HOD"
              onAssignClick={(cs, mode) => mode === 'view_details' ? setDetailTarget(cs) : setAssignTarget(cs)}
            />
          ) : null}
        </div>
      )}

      {/* ── Class Details & Attendance Roster Modal ── */}
      {detailTarget && (
        <HODClassDetailsModal
          classItem={detailTarget}
          onClose={() => setDetailTarget(null)}
        />
      )}

      {/* ── Single Assign Modal (from original HODDashboard AssignModal) ── */}
      {assignTarget && (
        <AssignModal
          classItem={assignTarget}
          deptFaculty={deptFaculty}
          onClose={() => setAssignTarget(null)}
          onAssigned={handleAssigned}
          classAPI={classAPI}
        />
      )}

      {/* ── Multi-Session Assign Modal ── */}
      {multiTarget && (
        <MultiAssignModal
          classes={multiTarget}
          deptFaculty={deptFaculty}
          onClose={() => setMultiTarget(null)}
          onAssigned={handleAssigned}
          classAPI={classAPI}
        />
      )}
    </div>
  );
}

/* ── CompletionTrackingTab ────────────────────────────────────────────────── */
function CompletionTrackingTab() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    classAPI.getHODClasses({})
      .then(d => setClasses(Array.isArray(d) ? d : (d.results || [])))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const g = { COMPLETED: [], ONGOING: [], PENDING: [], CANCELLED: [] };
    classes.forEach(c => { if (g[c.status]) g[c.status].push(c); });
    return g;
  }, [classes]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>Loading…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {Object.entries(grouped).map(([st, list]) => {
        const m = STATUS_META[st];
        return (
          <div key={st} style={{ background: '#fff', border: `1px solid ${m.bg}`, borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ background: m.bg, padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: m.color }}>{m.icon} {m.label} ({list.length})</span>
            </div>
            {list.length === 0 ? (
              <div style={{ padding: '20px 18px', color: '#94a3b8', fontSize: 13 }}>No classes.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead style={{ background: '#f8fafc' }}>
                    <tr>
                      {['Title', 'Faculty', 'Started At', 'Completed At', 'Note'].map(h => (
                        <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((c, i) => (
                      <tr key={c.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px', fontWeight: 600 }}>{c.title}</td>
                        <td style={{ padding: '10px 14px' }}>{c.assigned_to_name || <em style={{ color: '#94a3b8' }}>Unassigned</em>}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#475569' }}>{fmtDT(c.started_at)}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#475569' }}>{fmtDT(c.completed_at)}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8', maxWidth: 200 }}>{c.status_note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN HODDashboard COMPONENT
───────────────────────────────────────────────────────────────────────────── */
const HODDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Leave Management sub-tab
  const [activeLeaveTab, setActiveLeaveTab] = useState('my-applications');

  // Class Management sub-tab
  const [activeClassTab, setActiveClassTab] = useState('department-classes');

  // Dashboard data
  const [stats, setStats] = useState({
    total_professors: 0,
    total_students: 0,
    active_assignments: 0,
    pending_assignments: 0,
    recent_reassignments: 0,
    pending_leaves: 0,
    approved_leaves: 0,
  });

  const [professors,          setProfessors]          = useState([]);
  const [students,            setStudents]            = useState([]);
  const [studentsByProfessor, setStudentsByProfessor] = useState([]);

  // Modal states
  const [showReassignModal,    setShowReassignModal]    = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [selectedStudent,      setSelectedStudent]      = useState(null);
  const [selectedUserDetails,  setSelectedUserDetails]  = useState(null);
  const [selectedStudentForActivityModal, setSelectedStudentForActivityModal] = useState(null);

  // Sub-navigation states
  const [selectedProfessorForStudents, setSelectedProfessorForStudents] = useState(null);
  const [studentSearchQuery,           setStudentSearchQuery]           = useState('');
  const [studentSubtab,                setStudentSubtab]                = useState('mbbs');
  const [yearFilter,                   setYearFilter]                   = useState('');
  const [courseFilter,                 setCourseFilter]                 = useState('');
  const [professorSearchQuery,         setProfessorSearchQuery]         = useState('');

  // HOD Exam Marks Entry States
  const [examSchedules, setExamSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [marksDrafts, setMarksDrafts] = useState({});
  const [marksSaving, setMarksSaving] = useState(false);
  const [marksError, setMarksError] = useState('');
  const [marksSuccess, setMarksSuccess] = useState('');
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  const loadExamSchedules = async () => {
    try {
      setLoadingSchedules(true);
      const data = await hodExamAPI.getSchedules();
      setExamSchedules(data || []);
    } catch (err) {
      console.error('Error loading exam schedules:', err);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const handleSelectSchedule = async (scheduleId) => {
    setSelectedScheduleId(scheduleId);
    setAssignedStudents([]);
    setMarksDrafts({});
    setMarksError('');
    setMarksSuccess('');
    if (!scheduleId) return;

    try {
      setLoadingAssignments(true);
      const data = await hodExamAPI.getAssignments(scheduleId);
      setAssignedStudents(data || []);
      const drafts = {};
      (data || []).forEach(asg => {
        drafts[asg.id] = asg.obtained_marks !== null && asg.obtained_marks !== undefined ? asg.obtained_marks : '';
      });
      setMarksDrafts(drafts);
    } catch (err) {
      console.error('Error loading schedule assignments:', err);
      setMarksError('Failed to load assignments.');
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleMarkChange = (assignmentId, val) => {
    setMarksDrafts(prev => ({
      ...prev,
      [assignmentId]: val
    }));
  };

  const saveOrSubmitMarks = async (submitStatus) => {
    setMarksError('');
    setMarksSuccess('');
    if (!selectedScheduleId) return;

    const schedule = examSchedules.find(s => s.id === parseInt(selectedScheduleId));
    if (!schedule) return;

    const records = [];
    for (const student of assignedStudents) {
      const valStr = marksDrafts[student.id];
      if (valStr !== '' && valStr !== null && valStr !== undefined) {
        const val = parseFloat(valStr);
        if (isNaN(val) || val < 0 || val > schedule.max_marks) {
          setMarksError(`Marks for ${student.student_name} must be between 0 and ${schedule.max_marks}.`);
          return;
        }
        records.push({
          assignment_id: student.id,
          obtained_marks: val,
          status: submitStatus
        });
      } else {
        records.push({
          assignment_id: student.id,
          obtained_marks: null,
          status: submitStatus
        });
      }
    }

    try {
      setMarksSaving(true);
      await hodExamAPI.submitMarks(selectedScheduleId, records);
      setMarksSuccess(submitStatus === 'SUBMITTED' ? 'Marks submitted successfully!' : 'Draft saved successfully!');
      handleSelectSchedule(selectedScheduleId);
    } catch (err) {
      console.error('Error saving marks:', err);
      setMarksError(err.response?.data?.error || 'Failed to save marks.');
    } finally {
      setMarksSaving(false);
    }
  };

  /* ── Data loaders ── */
  const [hasHostelAllocation, setHasHostelAllocation] = useState(false);

  useEffect(() => {
    loadDashboardData();
    hostelInventoryAPI.getMyHostelProfile()
      .then(res => {
        if (res && res.allocated) {
          setHasHostelAllocation(true);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    switch (activeTab) {
      case 'professors':
        if (professors.length === 0) loadProfessors();
        break;
      case 'students':
        if (students.length === 0) loadStudents();
        break;
      case 'assignments':
        if (studentsByProfessor.length === 0) loadStudentsByProfessor();
        break;
      case 'marks':
        loadExamSchedules();
        break;
      default:
        break;
    }
  }, [activeTab]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, leaveStatsData] = await Promise.all([
        hodAPI.getDashboardStats(),
        leaveAPI.getLeaveStats(),
      ]);
      setStats({
        ...statsData,
        pending_leaves: leaveStatsData.total_pending,
        approved_leaves: leaveStatsData.total_approved,
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProfessors = async () => {
    try {
      const data = await hodAPI.getProfessors();
      const arr = data?.results || data || [];
      setProfessors(Array.isArray(arr) ? arr : []);
    } catch (error) {
      console.error('Error loading professors:', error);
    }
  };

  const loadStudents = async () => {
    try {
      const data = await hodAPI.getStudents();
      const arr = data?.results || data || [];
      setStudents(Array.isArray(arr) ? arr : []);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const loadStudentsByProfessor = async () => {
    try {
      const data = await hodAPI.getStudentsByProfessor();
      setStudentsByProfessor(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading students by professor:', error);
    }
  };

  /* ── Helpers ── */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleReassignStudent = async (student) => {
    if (professors.length === 0) await loadProfessors();
    setSelectedStudent(student);
    setShowReassignModal(true);
  };

  const handleViewUserDetails = async (userId, userType) => {
    try {
      let userDetails;
      if (userType === 'professor') {
        userDetails = await hodAPI.getProfessorDetail(userId);
      } else {
        userDetails = students.find(s => s.id === userId);
      }
      setSelectedUserDetails(userDetails);
      setShowUserDetailsModal(true);
    } catch (error) {
      console.error('Error loading user details:', error);
    }
  };

  const handleViewAssignedStudents = (professorGroup) => {
    setSelectedProfessorForStudents(professorGroup);
    setStudentSearchQuery('');
  };

  const handleBackToProfessors = () => {
    setSelectedProfessorForStudents(null);
    setStudentSearchQuery('');
  };

  const filteredProfessors = professors.filter(prof => {
    const q = professorSearchQuery.toLowerCase();
    return (
      prof.full_name?.toLowerCase().includes(q) ||
      prof.employee_id?.toLowerCase().includes(q) ||
      prof.email?.toLowerCase().includes(q)
    );
  });

  const getFilteredStudents = (list) => {
    if (!studentSearchQuery) return list;
    const q = studentSearchQuery.toLowerCase();
    return list.filter(s =>
      s.full_name?.toLowerCase().includes(q) ||
      s.student_id?.toLowerCase().includes(q)
    );
  };

  /* ── Loading state ── */
  if (loading) {
    return (
      <DashboardLayout title={`HOD Dashboard - ${user?.first_name} ${user?.last_name}`}>
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading dashboard...</p>
        </div>
      </DashboardLayout>
    );
  }

  /* ── Render ── */
  return (
    <DashboardLayout title={`Welcome, Dr. ${user?.last_name}!`}>
      <div className="dashboard-container">

        {/* Stats Grid */}
        <div className="stats-grid">
          <StatCard
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
            title="Total Professors"
            value={stats.total_professors}
            color="blue"
          />
          <StatCard
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
            title="Total Students"
            value={stats.total_students}
            color="green"
          />
          <StatCard
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            }
            title="Recent Reassignments"
            value={stats.recent_reassignments}
            color="orange"
          />
          <StatCard
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
            title="Pending Leave Requests"
            value={stats.pending_leaves}
            color="purple"
          />
        </div>

        {/* Main Tabs */}
        <div className="tabs-container">
          <div className="tabs">
            {[
              { key: 'overview',     label: 'Overview' },
              { key: 'professors',   label: 'Professors' },
              { key: 'students',     label: 'Students' },
              { key: 'assignments',  label: 'Student Assignments' },
              { key: 'classes',      label: 'Class Management' },
              { key: 'leave',        label: 'Leave Management' },
              { key: 'calendar',     label: '📅 Calendar' },
              { key: 'marks',        label: 'Exam Marks Entry' },
              { key: 'library',      label: '📚 My Library' },
              ...(hasHostelAllocation ? [
                { key: 'hostel',     label: '🏨 Hostel' }
              ] : []),
            ].map(t => (
              <button
                key={t.key}
                className={`tab ${activeTab === t.key ? 'active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">

          {/* ── Overview Tab ── */}
          {activeTab === 'overview' && (
            <div className="grid-2-col">
              <div className="card">
                <div className="card-header"><h2>Quick Actions</h2></div>
                <div className="card-body">
                  <div className="action-grid">
                    <button className="action-card" onClick={() => setActiveTab('professors')}>
                      <div className="action-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <h3>Manage Professors</h3>
                      <p>View and manage department faculty</p>
                    </button>

                    <button className="action-card" onClick={() => setActiveTab('assignments')}>
                      <div className="action-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </div>
                      <h3>Assign Students</h3>
                      <p>Assign or reassign students to professors</p>
                    </button>

                    <button className="action-card" onClick={() => setActiveTab('classes')}>
                      <div className="action-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                      <h3>Class Management</h3>
                      <p>Assign and track department classes</p>
                    </button>

                    <button className="action-card" onClick={() => setActiveTab('leave')}>
                      <div className="action-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3>Leave Management</h3>
                      <p>Review and approve leave requests</p>
                    </button>

                    <button className="action-card" onClick={() => setActiveTab('marks')}>
                      <div className="action-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3>Exam Marks Entry</h3>
                      <p>Enter & submit student exam marks</p>
                    </button>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header"><h2>Department Summary</h2></div>
                <div className="card-body">
                  <div className="summary-list">
                    <div className="summary-item">
                      <span className="summary-label">Professors</span>
                      <span className="summary-value">{stats.total_professors}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Students</span>
                      <span className="summary-value">{stats.total_students}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Active Class Assignments</span>
                      <span className="summary-value">{stats.active_assignments}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Pending Assignments</span>
                      <span className="summary-value">{stats.pending_assignments}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Pending Leave Requests</span>
                      <span className="summary-value badge-orange">{stats.pending_leaves}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Professors Tab ── */}
          {activeTab === 'professors' && (
            <div className="card">
              <div className="card-header">
                <h2>Department Professors</h2>
                <input
                  type="search"
                  placeholder="Search by name, email or employee ID..."
                  className="search-input"
                  value={professorSearchQuery}
                  onChange={(e) => setProfessorSearchQuery(e.target.value)}
                />
              </div>
              <div className="card-body">
                {filteredProfessors.length === 0 ? (
                  <p className="empty-state">
                    {professorSearchQuery ? 'No professors found matching your search' : 'No professors found'}
                  </p>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Employee ID</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Sub-Role</th>
                          <th>Assigned Students</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProfessors.map((professor) => (
                          <tr key={professor.id}>
                            <td>{professor.employee_id || 'N/A'}</td>
                            <td><strong>{professor.full_name}</strong></td>
                            <td>{professor.email}</td>
                            <td>
                              <span className="badge badge-blue">
                                {professor.subrole?.replace(/_/g, ' ') || 'Faculty'}
                              </span>
                            </td>
                            <td>{professor.assigned_students_count || 0}</td>
                            <td>
                              <button
                                className="btn-sm btn-outline"
                                onClick={() => handleViewUserDetails(professor.id, 'professor')}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Students Tab with MBBS & PG Subtabs ── */}
          {activeTab === 'students' && (() => {
            const mbbsList = students.filter(s => (s.course || s.profile?.course || 'MBBS') === 'MBBS');
            const pgList   = students.filter(s => (s.course || s.profile?.course) !== 'MBBS');

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Subtab Header Switcher Bar */}
                <div className="card" style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Students Directory & Roster</h2>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>Select MBBS or Postgraduate student registry below</p>
                  </div>

                  <div style={{ display: 'flex', background: '#f1f5f9', padding: 4, borderRadius: 10, border: '1px solid #cbd5e1' }}>
                    <button
                      onClick={() => setStudentSubtab('mbbs')}
                      style={{
                        padding: '7px 16px',
                        borderRadius: 7,
                        border: 'none',
                        background: studentSubtab === 'mbbs' ? '#ffffff' : 'transparent',
                        color: studentSubtab === 'mbbs' ? '#1d4ed8' : '#64748b',
                        fontWeight: studentSubtab === 'mbbs' ? 700 : 500,
                        fontSize: 13,
                        cursor: 'pointer',
                        boxShadow: studentSubtab === 'mbbs' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <span>🎓 MBBS Students</span>
                      <span style={{ padding: '1px 6px', borderRadius: 10, background: studentSubtab === 'mbbs' ? '#dbeafe' : '#e2e8f0', color: studentSubtab === 'mbbs' ? '#1e40af' : '#475569', fontSize: 11, fontWeight: 800 }}>{mbbsList.length}</span>
                    </button>

                    <button
                      onClick={() => setStudentSubtab('pg')}
                      style={{
                        padding: '7px 16px',
                        borderRadius: 7,
                        border: 'none',
                        background: studentSubtab === 'pg' ? '#ffffff' : 'transparent',
                        color: studentSubtab === 'pg' ? '#1d4ed8' : '#64748b',
                        fontWeight: studentSubtab === 'pg' ? 700 : 500,
                        fontSize: 13,
                        cursor: 'pointer',
                        boxShadow: studentSubtab === 'pg' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <span>🔬 Postgraduate (PG) Students</span>
                      <span style={{ padding: '1px 6px', borderRadius: 10, background: studentSubtab === 'pg' ? '#dbeafe' : '#e2e8f0', color: studentSubtab === 'pg' ? '#1e40af' : '#475569', fontSize: 11, fontWeight: 800 }}>{pgList.length}</span>
                    </button>
                  </div>
                </div>

                {/* SUBTAB 1: MBBS STUDENTS */}
                {studentSubtab === 'mbbs' && (() => {
                  const filteredMBBS = mbbsList.filter(student => {
                    if (studentSearchQuery && studentSearchQuery.trim()) {
                      const q = studentSearchQuery.toLowerCase();
                      const name = String(student.full_name || `${student.first_name || ''} ${student.last_name || ''}`).toLowerCase();
                      const sId = String(student.student_id || student.profile?.student_id || student.username || '').toLowerCase();
                      if (!name.includes(q) && !sId.includes(q)) return false;
                    }
                    if (yearFilter) {
                      const yr = String(student.mbbs_year || student.profile?.mbbs_year || student.year || '');
                      if (yr !== String(yearFilter)) return false;
                    }
                    return true;
                  });

                  return (
                    <div className="card">
                      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>🎓 MBBS Undergraduate Students Roster</h3>
                          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b' }}>Showing {filteredMBBS.length} of {mbbsList.length} total MBBS students across 1st to 4th years</p>
                        </div>
                        <div className="card-header-actions" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <select value={yearFilter} onChange={e => setYearFilter(e.target.value)} className="filter-select" style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', color: '#0f172a', fontWeight: 700 }}>
                            <option value="">All MBBS Years</option>
                            <option value="1">1st Year (MBBS)</option>
                            <option value="2">2nd Year (MBBS)</option>
                            <option value="3">3rd Year (MBBS)</option>
                            <option value="4">4th Year (MBBS)</option>
                          </select>

                          <input
                            type="search"
                            value={studentSearchQuery}
                            onChange={e => setStudentSearchQuery(e.target.value)}
                            placeholder="🔍 Search name or student ID..."
                            className="search-input"
                            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, minWidth: 220 }}
                          />
                        </div>
                      </div>
                      <div className="card-body">
                        {filteredMBBS.length === 0 ? (
                          <p className="empty-state">No MBBS students found matching your filters</p>
                        ) : (
                          <div className="table-container">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Student ID</th>
                                  <th>Name</th>
                                  <th>Course</th>
                                  <th>MBBS Year</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredMBBS.map((student) => {
                                  const yrNum = student.mbbs_year || student.profile?.mbbs_year || student.year;
                                  const yrLabel = yrNum ? `${yrNum}${yrNum === 1 ? 'st' : yrNum === 2 ? 'nd' : yrNum === 3 ? 'rd' : 'th'} Year` : '—';

                                  return (
                                    <tr key={student.id}>
                                      <td>{student.student_id || student.profile?.student_id || student.username || 'N/A'}</td>
                                      <td><strong>{student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || student.username}</strong></td>
                                      <td>
                                        <span className="badge badge-green">MBBS</span>
                                      </td>
                                      <td>
                                        <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#1d4ed8' }}>
                                          {yrLabel}
                                        </span>
                                      </td>
                                      <td>
                                        <div className="action-buttons">
                                          <button className="btn-sm btn-info" style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }} onClick={() => setSelectedStudentForActivityModal(student)}>View Activities</button>
                                          <button className="btn-sm btn-outline" onClick={() => handleViewUserDetails(student.id, 'student')}>View Details</button>
                                          <button className="btn-sm btn-primary" onClick={() => handleReassignStudent(student)}>Reassign</button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* SUBTAB 2: POSTGRADUATE (PG) STUDENTS */}
                {studentSubtab === 'pg' && (() => {
                  const isHOD = user?.role === 'HOD' || ['HEAD_OF_DEPARTMENT', 'PROFESSOR_HOD'].includes(user?.profile?.faculty_subrole);

                  const filteredPG = pgList.filter(student => {
                    if (studentSearchQuery && studentSearchQuery.trim()) {
                      const q = studentSearchQuery.toLowerCase();
                      const name = String(student.full_name || `${student.first_name || ''} ${student.last_name || ''}`).toLowerCase();
                      const sId = String(student.student_id || student.profile?.student_id || student.username || '').toLowerCase();
                      if (!name.includes(q) && !sId.includes(q)) return false;
                    }
                    if (courseFilter && student.course !== courseFilter && student.profile?.course !== courseFilter) return false;

                    if (isHOD) {
                      // HOD: Display only PG/SSPG students belonging to HOD's own department
                      const hodDeptId = user?.profile?.faculty_department?.id || user?.faculty_department_id;
                      const hodDeptName = (user?.profile?.faculty_department?.name || user?.department_name || '').toLowerCase();
                      const stdDeptId = student.profile?.student_department?.id || student.department_id;
                      const stdDeptName = (student.department_name || student.profile?.student_department?.name || '').toLowerCase();

                      if (hodDeptId && stdDeptId) {
                        if (String(hodDeptId) !== String(stdDeptId)) return false;
                      } else if (hodDeptName && stdDeptName) {
                        if (hodDeptName !== stdDeptName) return false;
                      }
                    } else {
                      // Non-HOD Faculty: Display ONLY PG/SSPG students who selected this faculty member during registration
                      const isMyStudent = student.assigned_professor_id === user?.id ||
                        (student.assigned_professor_name && user?.first_name && student.assigned_professor_name.toLowerCase().includes(user.first_name.toLowerCase()));
                      if (!isMyStudent) return false;
                    }

                    return true;
                  });

                  return (
                    <div className="card">
                      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>🔬 Postgraduate (PG) & Super Speciality Students Roster</h3>
                          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b' }}>Showing {filteredPG.length} of {pgList.length} total postgraduate residents and scholars</p>
                        </div>
                        <div className="card-header-actions" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <select value={courseFilter} onChange={e => setCourseFilter(e.target.value)} className="filter-select" style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', color: '#0f172a', fontWeight: 600 }}>
                            <option value="">All PG Programs</option>
                            <option value="PG">PG (MD / MS)</option>
                            <option value="SUPER_SPECIALITY">Super Speciality (DM / MCh)</option>
                          </select>

                          <input
                            type="search"
                            value={studentSearchQuery}
                            onChange={e => setStudentSearchQuery(e.target.value)}
                            placeholder="🔍 Search name or student ID..."
                            className="search-input"
                            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, minWidth: 220 }}
                          />
                        </div>
                      </div>
                      <div className="card-body">
                        {filteredPG.length === 0 ? (
                          <p className="empty-state">No Postgraduate (PG) students found matching your filters</p>
                        ) : (
                          <div className="table-container">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Student ID</th>
                                  <th>Name</th>
                                  <th>Course / Specialty</th>
                                  <th>Department</th>
                                  <th>Assigned Guide / Professor</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredPG.map((student) => {
                                  const progName = student.course_display || student.course || student.profile?.course || 'PG Resident';
                                  const deptName = student.department_name || student.profile?.student_department?.name || '—';

                                  return (
                                    <tr key={student.id}>
                                      <td>{student.student_id || student.profile?.student_id || student.username || 'N/A'}</td>
                                      <td><strong>{student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || student.username}</strong></td>
                                      <td>
                                        <span className="badge badge-purple" style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: '#f3e8ff', color: '#6b21a8' }}>
                                          {progName}
                                        </span>
                                      </td>
                                      <td style={{ fontSize: 12.5, color: '#475569' }}>{deptName}</td>
                                      <td>{student.assigned_professor_name || 'Not assigned'}</td>
                                      <td>
                                        <div className="action-buttons">
                                          <button className="btn-sm btn-info" style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }} onClick={() => setSelectedStudentForActivityModal(student)}>View Activities</button>
                                          <button className="btn-sm btn-outline" onClick={() => handleViewUserDetails(student.id, 'student')}>View Details</button>
                                          <button className="btn-sm btn-primary" onClick={() => handleReassignStudent(student)}>Reassign Guide</button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}


          {/* ── Student Assignments Tab ── */}
          {activeTab === 'assignments' && (
            <div className="card">
              {!selectedProfessorForStudents ? (
                <>
                  <div className="card-header">
                    <h2>Student-Professor Assignments</h2>
                    <p className="card-subtitle">Click "View Assigned Students" to see students for each professor</p>
                  </div>
                  <div className="card-body">
                    {studentsByProfessor.length === 0 ? (
                      <p className="empty-state">Loading assignments...</p>
                    ) : (
                      <div className="professors-assignment-list">
                        {studentsByProfessor.map((group, index) => (
                          <div key={index} className="professor-assignment-card">
                            <div className="professor-assignment-info">
                              <div className="professor-avatar">
                                {group.professor.full_name?.charAt(0) || '?'}
                              </div>
                              <div className="professor-details">
                                <h3>{group.professor.full_name}</h3>
                                {group.professor.employee_id && (
                                  <p className="professor-id">Employee ID: {group.professor.employee_id}</p>
                                )}
                                <div className="professor-stats">
                                  <span className="stat-badge">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                    {group.student_count} Student{group.student_count !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <button className="btn-view-students" onClick={() => handleViewAssignedStudents(group)}>
                              View Assigned Students
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="card-header">
                    <div className="header-with-back">
                      <button className="btn-back" onClick={handleBackToProfessors}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Professors
                      </button>
                      <div>
                        <h2>Students assigned to {selectedProfessorForStudents.professor.full_name}</h2>
                        {selectedProfessorForStudents.professor.employee_id && (
                          <p className="card-subtitle">Employee ID: {selectedProfessorForStudents.professor.employee_id}</p>
                        )}
                      </div>
                    </div>
                    <input
                      type="search"
                      placeholder="Search by student name or ID..."
                      className="search-input"
                      value={studentSearchQuery}
                      onChange={(e) => setStudentSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="card-body">
                    {getFilteredStudents(selectedProfessorForStudents.students).length === 0 ? (
                      <p className="empty-state">
                        {studentSearchQuery ? 'No students found matching your search' : 'No students assigned'}
                      </p>
                    ) : (
                      <div className="table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Student ID</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Course</th>
                              <th>Username</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredStudents(selectedProfessorForStudents.students).map((student) => (
                              <tr key={student.id}>
                                <td>{student.student_id || 'N/A'}</td>
                                <td><strong>{student.full_name}</strong></td>
                                <td>{student.email}</td>
                                <td>
                                  <span className="badge badge-green">
                                    {student.course_display || student.course}
                                  </span>
                                </td>
                                <td>{student.username}</td>
                                <td>
                                  <div className="action-buttons">
                                    <button
                                      className="btn-sm btn-info"
                                      style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                                      onClick={() => setSelectedStudentForActivityModal(student)}
                                    >
                                      View Activities
                                    </button>
                                    <button
                                      className="btn-sm btn-outline"
                                      onClick={() => handleViewUserDetails(student.id, 'student')}
                                    >
                                      View Details
                                    </button>
                                    <button
                                      className="btn-sm btn-primary"
                                      onClick={() => handleReassignStudent(student)}
                                    >
                                      Reassign
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Class Management Tab (enhanced from File 2) ── */}
          {activeTab === 'classes' && (
            <div className="card full-width-card">
              {/* Sub-tabs */}
              <div className="card-header-with-tabs">
                <div className="subtabs-container">
                  <button
                    className={`subtab ${activeClassTab === 'department-classes' ? 'active' : ''}`}
                    onClick={() => setActiveClassTab('department-classes')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Department Classes
                  </button>
                  <button
                    className={`subtab ${activeClassTab === 'completion-tracking' ? 'active' : ''}`}
                    onClick={() => setActiveClassTab('completion-tracking')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Completion Tracking
                  </button>
                  <button
                      className={`subtab ${activeClassTab === 'attendance-summary' ? 'active' : ''}`}
                      onClick={() => setActiveClassTab('attendance-summary')}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Attendance Summary
                    </button>
                </div>
              </div>

              <div className="card-body fullscreen-content">
                {activeClassTab === 'department-classes'  && <HODClassesTab classAPI={classAPI} />}
                {activeClassTab === 'completion-tracking' && <CompletionTrackingTab />}
                {activeClassTab === 'attendance-summary' && <HODAttendanceSummaryTab />}
              </div>
            </div>
          )}

          {/* ── Leave Management Tab ── */}
          {activeTab === 'leave' && (
            <div className="card full-width-card">
              <div className="card-header-with-tabs">
                <div className="subtabs-container">
                  <button
                    className={`subtab ${activeLeaveTab === 'my-applications' ? 'active' : ''}`}
                    onClick={() => setActiveLeaveTab('my-applications')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    My Leave Applications
                  </button>
                  <button
                    className={`subtab ${activeLeaveTab === 'approve-requests' ? 'active' : ''}`}
                    onClick={() => setActiveLeaveTab('approve-requests')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Approve Leave Requests
                    {stats.pending_leaves > 0 && (
                      <span className="badge badge-orange tab-badge">{stats.pending_leaves}</span>
                    )}
                  </button>
                </div>
              </div>

              <div className="card-body fullscreen-content">
                {activeLeaveTab === 'my-applications'  && <LeaveManagementSidebar />}
                {activeLeaveTab === 'approve-requests' && <ApproveLeaves showDepartmentView={true} />}
              </div>
            </div>
          )}

          {/* ── Exam Marks Entry Tab ── */}
          {activeTab === 'marks' && (
            <div className="card full-width-card" style={{ padding: '24px', background: '#ffffff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Exam Marks Entry</h2>
                  <p style={{ fontSize: '14px', color: '#64748b', margin: '4px 0 0 0' }}>Assign and modify marks for Practical and Internal examinations.</p>
                </div>
              </div>

              {/* Schedule Selector */}
              <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '.04em' }}>Select Examination Schedule</label>
                {loadingSchedules ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b' }}>
                    <div className="spinner-small"></div> Loading schedules...
                  </div>
                ) : (
                  <select
                    value={selectedScheduleId}
                    onChange={(e) => handleSelectSchedule(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '550px',
                      padding: '10px 14px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: '#fff',
                      color: '#0f172a',
                      fontFamily: 'inherit',
                      outline: 'none',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                  >
                    <option value="">— Select an exam schedule —</option>
                    {examSchedules.map(sch => (
                      <option key={sch.id} value={sch.id}>
                        {sch.subject} ({sch.exam_type_display} · {sch.sub_type_display}) — {fmtDate(sch.exam_date)}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Assignments / Marks Input Table */}
              {selectedScheduleId && (
                <div>
                  {loadingAssignments ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '24px 0', color: '#64748b' }}>
                      <div className="spinner-small"></div> Loading assigned students...
                    </div>
                  ) : (
                    <div>
                      {/* Schedule info summary card */}
                      {(() => {
                        const selectedSch = examSchedules.find(s => s.id === parseInt(selectedScheduleId));
                        if (!selectedSch) return null;
                        return (
                          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '24px', display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>Subject</div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{selectedSch.subject}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>Exam Type</div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{selectedSch.exam_type_display} ({selectedSch.sub_type_display})</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>Max Marks</div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{selectedSch.max_marks}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>Pass Marks</div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{selectedSch.pass_marks}</div>
                            </div>
                          </div>
                        );
                      })()}

                      {marksError && (
                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px', fontWeight: '500' }}>
                          ⚠️ {marksError}
                        </div>
                      )}

                      {marksSuccess && (
                        <div style={{ background: '#dcfce7', border: '1px solid #b2dcc8', color: '#166534', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px', fontWeight: '500' }}>
                          ✓ {marksSuccess}
                        </div>
                      )}

                      {assignedStudents.length === 0 ? (
                        <div style={{ padding: '32px', textAlign: 'center', background: '#f8fafc', borderRadius: '12px', color: '#64748b' }}>
                          No students assigned to this examination.
                        </div>
                      ) : (
                        <div>
                          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px', marginBottom: '24px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px' }}>
                              <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                  <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Roll Number</th>
                                  <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Student Name</th>
                                  <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Attendance</th>
                                  <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Obtained Marks</th>
                                  <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Status</th>
                                  <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Last Entered By</th>
                                </tr>
                              </thead>
                              <tbody>
                                {assignedStudents.map((student) => {
                                  const maxMarks = examSchedules.find(s => s.id === parseInt(selectedScheduleId))?.max_marks || 100;
                                  return (
                                    <tr key={student.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.1s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                      <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>{student.student_id_num || student.roll_number || '—'}</td>
                                      <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>{student.student_name}</td>
                                      <td style={{ padding: '14px 16px' }}>
                                        <span style={{
                                          display: 'inline-block',
                                          padding: '3px 10px',
                                          borderRadius: '20px',
                                          fontSize: '11px',
                                          fontWeight: '700',
                                          background: student.attendance === 'PRESENT' ? '#dcfce7' : student.attendance === 'ABSENT' ? '#fde2e2' : '#f1f5f9',
                                          color: student.attendance === 'PRESENT' ? '#166534' : student.attendance === 'ABSENT' ? '#991b1b' : '#475569'
                                        }}>
                                          {student.attendance_display || student.attendance}
                                        </span>
                                      </td>
                                      <td style={{ padding: '14px 16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <input
                                            type="number"
                                            value={marksDrafts[student.id] !== undefined ? marksDrafts[student.id] : ''}
                                            onChange={(e) => handleMarkChange(student.id, e.target.value)}
                                            disabled={student.attendance === 'ABSENT'}
                                            placeholder={student.attendance === 'ABSENT' ? 'ABS' : '0.0'}
                                            min="0"
                                            max={maxMarks}
                                            step="0.5"
                                            style={{
                                              width: '80px',
                                              padding: '6px 10px',
                                              border: '1px solid #e2e8f0',
                                              borderRadius: '6px',
                                              fontSize: '14px',
                                              color: '#0f172a',
                                              fontWeight: '600',
                                              textAlign: 'center',
                                              outline: 'none',
                                              background: student.attendance === 'ABSENT' ? '#f1f5f9' : '#fff'
                                            }}
                                          />
                                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>/ {maxMarks}</span>
                                        </div>
                                      </td>
                                      <td style={{ padding: '14px 16px' }}>
                                        <span style={{
                                          display: 'inline-block',
                                          padding: '3px 10px',
                                          borderRadius: '20px',
                                          fontSize: '11px',
                                          fontWeight: '700',
                                          background: student.marks_status === 'SUBMITTED' ? '#dbeafe' : student.marks_status === 'PENDING' ? '#fef3c7' : '#f1f5f9',
                                          color: student.marks_status === 'SUBMITTED' ? '#1d4ed8' : student.marks_status === 'PENDING' ? '#92400e' : '#475569'
                                        }}>
                                          {student.marks_status === 'SUBMITTED' ? 'Submitted' : student.marks_status === 'PENDING' ? 'Draft' : 'Not Entered'}
                                        </span>
                                      </td>
                                      <td style={{ padding: '14px 16px', fontSize: '12px', color: '#64748b' }}>
                                        {student.marks_entered_by_name ? (
                                          <div>
                                            <strong>{student.marks_entered_by_name}</strong>
                                            <div style={{ fontSize: '10px', color: '#94a3b8' }}>{fmtDT(student.marks_entered_at)}</div>
                                          </div>
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* Action Buttons */}
                          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => saveOrSubmitMarks('PENDING')}
                              disabled={marksSaving}
                              style={{
                                padding: '10px 20px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                background: '#fff',
                                color: '#374151',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                outline: 'none'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                              onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                            >
                              {marksSaving ? 'Saving...' : 'Save Draft'}
                            </button>
                            <button
                              onClick={() => saveOrSubmitMarks('SUBMITTED')}
                              disabled={marksSaving}
                              style={{
                                padding: '10px 24px',
                                border: 'none',
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #1d4ed8, #4f46e5)',
                                color: '#fff',
                                fontSize: '14px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                outline: 'none',
                                boxShadow: '0 4px 12px rgba(29, 78, 216, 0.2)'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                              {marksSaving ? 'Submitting...' : 'Final Submit'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {activeTab === 'library' && (
            <MyBorrowedBooks />
          )}
          {activeTab === 'hostel' && (
            <HostelUserPortalTab />
          )}
          {activeTab === 'calendar' && (
            <div className="card" style={{ padding: 0, overflow: 'visible' }}>
              <Calendar />
            </div>
          )}

        </div>{/* end tab-content */}
      </div>{/* end dashboard-container */}

      {/* ── Modals ── */}
      {showReassignModal && selectedStudent && (
        <ReassignStudentModal
          student={selectedStudent}
          professors={professors}
          onClose={() => { setShowReassignModal(false); setSelectedStudent(null); }}
          onSuccess={() => {
            setShowReassignModal(false);
            setSelectedStudent(null);
            loadStudents();
            loadStudentsByProfessor();
            loadDashboardData();
          }}
        />
      )}

      {showUserDetailsModal && selectedUserDetails && (
        <UserDetailsModal
          user={selectedUserDetails}
          onClose={() => { setShowUserDetailsModal(false); setSelectedUserDetails(null); }}
        />
      )}

      {selectedStudentForActivityModal && (
        <StudentActivityModal
          student={selectedStudentForActivityModal}
          onClose={() => setSelectedStudentForActivityModal(null)}
          user={user}
          fromSource="HOD"
        />
      )}

    </DashboardLayout>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   USER DETAILS MODAL
───────────────────────────────────────────────────────────────────────────── */
const UserDetailsModal = ({ user, onClose }) => {
  const getFacultySubroleDisplay = (subrole) => {
    const map = {
      'SENIOR_RESIDENT':    'Senior Resident',
      'ASSISTANT_PROFESSOR':'Assistant Professor',
      'ASSOCIATE_PROFESSOR':'Associate Professor',
      'PROFESSOR':          'Professor',
      'HEAD_OF_DEPARTMENT': 'Head of Department',
      'PROFESSOR_HOD':      'Professor + HOD',
      'DEAN':               'Dean',
    };
    return map[subrole] || subrole;
  };

  const getGenderDisplay = (g) => ({ M: 'Male', F: 'Female', O: 'Other' }[g] || g);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>User Details</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="professor-details-grid">

            {/* Personal Information */}
            <div className="details-section">
              <h3 className="section-title">Personal Information</h3>
              <div className="details-list">
                <div className="detail-item">
                  <span className="detail-label">Full Name:</span>
                  <span className="detail-value">{user.first_name} {user.last_name}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Username:</span>
                  <span className="detail-value">{user.username}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{user.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{user.phone_number || 'Not provided'}</span>
                </div>
                {user.profile?.date_of_birth && (
                  <div className="detail-item">
                    <span className="detail-label">Date of Birth:</span>
                    <span className="detail-value">{formatDate(user.profile.date_of_birth)}</span>
                  </div>
                )}
                {user.profile?.gender && (
                  <div className="detail-item">
                    <span className="detail-label">Gender:</span>
                    <span className="detail-value">{getGenderDisplay(user.profile.gender)}</span>
                  </div>
                )}
                {user.profile?.blood_group && (
                  <div className="detail-item">
                    <span className="detail-label">Blood Group:</span>
                    <span className="detail-value">{user.profile.blood_group}</span>
                  </div>
                )}
                {user.profile?.emergency_contact && (
                  <div className="detail-item">
                    <span className="detail-label">Emergency Contact:</span>
                    <span className="detail-value">{user.profile.emergency_contact}</span>
                  </div>
                )}
                {user.profile?.address && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Address:</span>
                    <span className="detail-value">{user.profile.address}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Student Academic Information */}
            {user.role === 'STUDENT' && (
              <div className="details-section">
                <h3 className="section-title">Academic Information</h3>
                <div className="details-list">
                  <div className="detail-item">
                    <span className="detail-label">Student ID:</span>
                    <span className="detail-value">{user.profile?.student_id || user.student_id || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Course:</span>
                    <span className="detail-value">{user.profile?.course || user.course || 'N/A'}</span>
                  </div>
                  {user.profile?.course !== 'MBBS' && (user.profile?.student_department_name || user.student_department_name) && (
                    <div className="detail-item">
                      <span className="detail-label">Department:</span>
                      <span className="detail-value">{user.profile?.student_department_name || user.student_department_name}</span>
                    </div>
                  )}
                  {user.profile?.enrollment_year && (
                    <div className="detail-item">
                      <span className="detail-label">Enrollment Year:</span>
                      <span className="detail-value">{user.profile.enrollment_year}</span>
                    </div>
                  )}
                  {user.profile?.current_semester && (
                    <div className="detail-item">
                      <span className="detail-label">Current Semester:</span>
                      <span className="detail-value">{user.profile.current_semester}</span>
                    </div>
                  )}
                  {user.profile?.entrance_score && (
                    <div className="detail-item">
                      <span className="detail-label">Entrance Score:</span>
                      <span className="detail-value">{user.profile.entrance_score}%</span>
                    </div>
                  )}
                  {(user.profile?.assigned_professor_name || user.assigned_professor_name) && (
                    <div className="detail-item">
                      <span className="detail-label">Assigned Professor:</span>
                      <span className="detail-value">{user.profile?.assigned_professor_name || user.assigned_professor_name}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Faculty / Staff Professional Information */}
            {['FACULTY', 'ADMIN', 'FINANCE', 'HOSTEL_ADMIN', 'LIBRARY_STAFF', 'TRANSPORT_MANAGER'].includes(user.role) && (
              <div className="details-section">
                <h3 className="section-title">Professional Information</h3>
                <div className="details-list">
                  <div className="detail-item">
                    <span className="detail-label">Employee ID:</span>
                    <span className="detail-value">{user.profile?.employee_id || user.employee_id || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Role:</span>
                    <span className="badge badge-blue">{user.role}</span>
                  </div>
                  {user.role === 'FACULTY' && (user.profile?.faculty_subrole || user.subrole) && (
                    <div className="detail-item">
                      <span className="detail-label">Sub-Role:</span>
                      <span className="detail-value">
                        {getFacultySubroleDisplay(user.profile?.faculty_subrole || user.subrole)}
                      </span>
                    </div>
                  )}
                  {(user.profile?.faculty_department_name || user.faculty_department_name) && (
                    <div className="detail-item">
                      <span className="detail-label">Department:</span>
                      <span className="detail-value">{user.profile?.faculty_department_name || user.faculty_department_name}</span>
                    </div>
                  )}
                  {user.profile?.joining_date && (
                    <div className="detail-item">
                      <span className="detail-label">Joining Date:</span>
                      <span className="detail-value">{formatDate(user.profile.joining_date)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Account Status */}
            <div className="details-section full-width">
              <h3 className="section-title">Account Status</h3>
              <div className="details-list">
                <div className="detail-item">
                  <span className="detail-label">Account Status:</span>
                  <span className={`badge badge-${user.approval_status === 'APPROVED' ? 'green' : user.approval_status === 'PENDING' ? 'yellow' : 'red'}`}>
                    {user.approval_status}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Account Created:</span>
                  <span className="detail-value">{formatDate(user.created_at)}</span>
                </div>
                {(user.approved_by_name || user.approved_by) && (
                  <div className="detail-item">
                    <span className="detail-label">Approved By:</span>
                    <span className="detail-value">{user.approved_by_name || user.approved_by}</span>
                  </div>
                )}
                {user.last_login && (
                  <div className="detail-item">
                    <span className="detail-label">Last Login:</span>
                    <span className="detail-value">{formatDate(user.last_login)}</span>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   REASSIGN STUDENT MODAL
───────────────────────────────────────────────────────────────────────────── */
const ReassignStudentModal = ({ student, professors, onClose, onSuccess }) => {
  const [selectedProfessor, setSelectedProfessor] = useState('');
  const [reason,            setReason]            = useState('');
  const [submitting,        setSubmitting]         = useState(false);
  const [error,             setError]             = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProfessor) { setError('Please select a professor'); return; }
    setSubmitting(true); setError('');
    try {
      await hodAPI.reassignStudent(student.id, parseInt(selectedProfessor), reason);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reassign student');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Reassign Student</h2>
            <p className="modal-subtitle">{student.full_name} ({student.student_id})</p>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-message">{error}</div>}

            <div className="info-box">
              <p><strong>Current Professor:</strong> {student.assigned_professor_name || 'Not assigned'}</p>
              <p><strong>Course:</strong> {student.course_display || student.course}</p>
            </div>

            <div className="form-group">
              <label htmlFor="professor">New Professor *</label>
              <select
                id="professor"
                value={selectedProfessor}
                onChange={(e) => setSelectedProfessor(e.target.value)}
                required
              >
                <option value="">Select Professor</option>
                {professors
                  .filter(p => p.id !== student.assigned_professor_id)
                  .map((prof) => (
                    <option key={prof.id} value={prof.id}>
                      {prof.full_name} ({prof.employee_id}) - {prof.assigned_students_count} students
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="reason">Reason for Reassignment</label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows="3"
                placeholder="Optional: Explain why this reassignment is being made"
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Reassigning...' : 'Confirm Reassignment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HODDashboard;